import { OrderSource } from '../ticket/ticket.types';

export interface createPaymentResponseType {
  success: boolean;
  data: createPaymentResponse;
  message: string;
  error?: string | null;
}

export interface updatePaymentTipResponseType {
  success: boolean;
  message: string;
  error?: string | null;
}

export interface createPaymentResponse {
  paymentMethod: string;
  tax: number;
  discount: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  subTotal: number;
  transactionType: string;
  transactionAmount: number;
  id: number;
  createdAt: Date;
  updatedAt: Date;
  ticketId: number;
}

export interface cardPaymentTriggerResponseType {
  success: boolean;
  data: cardPaymentTriggerResponse;
  message: string;
  error?: string | null;
}

export interface cardPaymentTriggerResponse {
  paymentMethod: string;
  tax: number;
  discount: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  subTotal: number;
  transactionAmount: number;
  transactionType: TransactionType;
  id: number;
  createdAt: Date;
  updatedAt: Date;
  ticketId?: number;
  cardDetails?: CardDetailsResponse;
}

export interface CardDetailsResponse {
  last4: number;
  expYear: number;
  expMonth: number;
  cardHolderName: string;
}

export type PaymentType = {
  paymentMethod: string;
  tax: number;
  discount: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  paid: number;
  id: number;
  createdAt: Date;
  updatedAt: Date;
  cardPaymentTriggerLogs?: cardPaymentTriggerResponse[];
};

export type RefundType = {
  id: number;
  paymentMethod: PaymentMethod;
  chargeId: string;
  originalTransactionId: string;
  amount: number;
  tax: number;
  tips: number;
  serviceCharges: number;
  convenienceFee: number;
  reason: string;
  externalRefundId: string;
  createdAt: Date;
  updatedAt: Date;
  refundStatus: PaymentStatus;
};

export type getPaymentResponseType = Omit<createPaymentResponseType, 'data'> & {
  data: PaymentType[];
};

export interface cardPaymentTriggerRequestType {
  tax: number;
  discount: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  subTotal: number;
  serviceCharges: number[];
}

export interface cardRefundTriggerRequestType {
  tax: number;
  transactionId: string;
  chargeId: string;
  initialPaymentId: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  transactionAmount: number;
  ticketItems: { id: number; amount: number; quantity: number }[];
}

export interface createPaymentRequestType {
  paymentMethod: string;
  tax: number;
  discount: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  paid: number;
  serviceCharges: number[];
}

export interface updatePaymentTipRequestType {
  tips: number;
}
export interface GetTicketDetailsPaymentData {
  id: number;
  paid: number;
  tax: number;
  refundableTax: number;
  tip: number;
  paymentMethod: PaymentMethod;
  createdAt: Date;
  cardDetails: CardDetailsResponse;
  transactionAmount: number | null;
  subTotal: number;
  refundReason: string | null;
  chargeId: string;
  total: number;
  refundId: null | string;
  refund: RefundType[];
  transactionId: string;
  refundableAmount: number;
  refundableTip: number;
  paymentStatus: PaymentStatus;
  paymentSource: OnlinePaymentSource;
}

export interface PaymentIntentType {
  cartId: number;
  restaurantId: number;
  app: string;
}

export interface createZeroPaymentResponseType {
  success: boolean;
  data: createZeroPaymentResponse | null;
  message: string;
  error?: string | null;
}

export interface createZeroPaymentResponse {
  paymentMethod: string;
  tax: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  subTotal: number;
  transactionType: string;
  transactionAmount: number;
  id: number;
  createdAt: Date;
  updatedAt: Date;
  ticketId: number;
}

export interface createZeroDollarPaymentRequestType {
  paymentMethod: string;
  tax: number;
  convenienceFee: number;
  tips: number;
  paymentStatus: string;
  total: number;
  paid: number;
  subTotal: number;
  discount: number;
  orderSource: OrderSource;
  restaurantId?: number;
}

export interface PaymentIntentResponseDataType {
  clientSecret: string;
}
export interface PaymentIntentResponseType {
  data?: PaymentIntentResponseDataType | null;
}

export interface StripeKeyResponseDataType {
  publishableKey: string;
}

export interface StripeKeyResponseType {
  data?: StripeKeyResponseDataType | null;
}

export interface CardDetailsType {
  last4: number;
  cardHolderName: string;
  fingerprint?: string;
  expMonth: number;
  expYear: number;
  aid?: string;
  cvm?: string;
  inputMethod?: string;
  paymentSource?: string;
  adyenFingerprint?: string; // alias for adyen payment source
}

export enum PaymentCurrency {
  USD = 'usd',
}

export enum PaymentCaptureMethod {
  Automatic = 'automatic',
  Manual = 'manual',
  AutomaticAsync = 'automatic_async',
}

export enum PaymentMethod {
  Cash = 'cash',
  Card = 'card',
  Tender = 'tender',
  Gift_Card = 'gift-card',
  MKE = 'mke',
}

export enum GetPaymentWebhookType {
  Payment = 'payment',
  Refund = 'refund',
}

export enum PaymentWebhookResponseType {
  Success = 'success',
  Failure = 'failure',
}

export enum CardPaymentEventMessageType {
  PaymentFailed = 'Payment Failed',
  PaymentSuccess = 'Payment Success',
  PaymentUncaptured = 'Payment Uncaptured',
  RefundSuccess = 'Refund Success',
  RefundFailed = 'Refund Failed',
}

export enum PaymentWebhookType {
  PaymentIntentCreated = 'payment_intent.created',
  PaymentIntentSuccess = 'payment_intent.succeeded',
  PaymentIntentCanceled = 'payment_intent.canceled',
  PaymentIntentFailed = 'payment_intent.payment_failed',
  PaymentIntentAmountCaptureableUpdate = 'payment_intent.amount_capturable_updated',
  ChargeCreated = 'charge.succeeded',
  ChargeFailed = 'charge.failed',
  ChargeRefunded = 'charge.refunded',
  TransferCreated = 'transfer.created',
  TransferReversed = 'transfer.reversed',
  // Dispute
  NotificationOfChargeback = 'NOTIFICATION_OF_CHARGEBACK',
  Chargeback = 'CHARGEBACK',
  ChargebackReversed = 'CHARGEBACK_REVERSED',
  SecondChargeback = 'SECOND_CHARGEBACK',

  // Transactions
  TransactionCreated = 'balancePlatform.transaction.created',
}

export enum WebhookOperations {
  PAYMENT = 'payment',
  REFUND = 'refund',
  TRANSFER = 'transfer',
  DISPUTE = 'dispute',
}

export enum ConfirmationMethodType {
  Automatic = 'automatic',
  Manual = 'manual',
}

export enum TransferReversalStatusType {
  PartialReversal = 'partial_reversal',
  FullReversal = 'full_reversal',
}

export enum PaymentStatus {
  FullPaid = 'full-paid',
  PartialPaid = 'partial-paid',
  RefundInitiated = 'refund-initiated',
  PartialRefund = 'partial-refund',
  FullRefund = 'full-refund',
}

export enum ReceiptType {
  PaymentReceipt = 'payment',
  RefundReceipt = 'refund',
}

export enum PaymentState {
  Paid = 'paid',
  Unclosed = 'unclosed',
  Canceled = 'canceled',
  CanceledInitiated = 'canceled-initiated',
  Failed = 'failed',
}

export enum TransactionType {
  Refund = 'refund',
  Charge = 'charge',
}

export enum OnlinePaymentSource {
  Stripe = 'stripe',
  Adyen = 'adyen',
}

export enum CaptureStatus {
  PendingCapture = 'pending-capture',
  Captured = 'captured',
  FailedCapture = 'failed-capture',
  Cancelled = 'cancelled',
}

export enum ConvenienceFeeTypes {
  Terminal = 'terminal',
  Nfc = 'nfc',
  Online = 'online',
  All = 'all',
}
export enum PaymentLabel {
  CASH = 'Cash',
  CARD = 'Credit card',
  TENDER = 'Tenders',
  GIFT_CARD = 'Gift card',
  THIRD_PARTY_ORDER = '3rd Party Orders',
}

export enum PaymentLogType {
  TipAdded = 'tip-added',
  TipAddedError = 'tip-added-error',

  TipAdjusted = 'tip-adjusted',
  TipAdjustedError = 'tip-adjusted-error',

  PaymentAdded = 'payment-added',

  PaymentAdjusted = 'payment-adjusted',
  PaymentAdjustedError = 'payment-adjusted-error',

  RefundProcessed = 'refund-processed',
  RefundProcessedError = 'refund-processed-error',

  CancelPaymentInitiated = 'cancel-payment-initiated',
  CancelPayment = 'cancel-payment',
  CancelPaymentError = 'cancel-payment-error',
}

export type RefundAdjustableField =
  | 'refundedAmount'
  | 'refundedGratuityAmount'
  | 'refundedserviceChargeAmount'
  | 'refundedTax'
  | 'refundedTip';

export enum AdyenSessionRoles {
  PAYOUTS_OVERVIEW_COMPONENT_VIEW = 'Payouts Overview Component: View',
  DISPUTES_COMPONENT_MANAGE = 'Disputes Component: Manage',
}
